import React, { useState, useEffect, useRef } from 'react';
import View from './view';
import { FILE_CMD } from 'hooks/useChat';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import _ from 'lodash';
import { useReduxForm } from 'hooks/useReduxForm';

const ChatWindow = (props: any) => {
  const {
    bubble,
    patient,
    assistant,
    allowUpload = true,
    groupedMessages,
    markMessagesRead,
    unreadMessages,
    disabledInput = false,
    allowAssistant = false,
    extraIcons,
    connected = true,
    streamingText,
    loadingReply,
    stopRequest,
    runSavedPromptTyped,
    replySuggestions,
    welcomeMessage,
    isCareyRoom = false,
  } = props;

  const roomName = bubble?.room_name || bubble?.id || props.room?.id;

  const messagesEndRef = useRef<HTMLElement | null>(null);
  const dispatch = useAppDispatch();

  const isLoadingArchives = useIsLoading(api.Chat.getUserArchives);

  let formName = props.form || `ChatWindow-${patient?.id}`;
  if (bubble !== undefined) formName = `ChatWindow-${bubble.id}`;

  const { setValue, watch } = useReduxForm(formName, {
    destroyOnUnmount: false,
    defaultValues: { currentMessage: '' },
  });

  const currentMessage = watch('currentMessage') || '';

  const [disabledChat, setDisabledChat] = useState(
    assistant ? false : bubble === undefined || !bubble.chat,
  );

  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);

  useEffect(() => {
    if (streamingText) {
      setCurrentMessage(streamingText);
    }
  }, [streamingText]);

  useEffect(() => {
    if (roomName && unreadMessages[roomName] > 0) {
      markMessagesRead(roomName);
    }
  }, [unreadMessages, markMessagesRead, roomName]);

  const scrollDep = assistant ? groupedMessages.length : groupedMessages;

  useEffect(() => scrollToBottom(), [scrollDep]);

  function setMessagesEnd(element: HTMLElement | null) {
    messagesEndRef.current = element;
  }

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView();
  }

  async function uploadFiles(files: Blob[] = []) {
    setUploadingFiles(true);
    for (const file of files) {
      let result = await dispatch(api.Chat.uploadChatFile({ file, chatId: roomName }), {
        errorTitle: 'File Upload',
      });
      // In Carey rooms, the backend posts a document_received artifact card on upload,
      // so the legacy `/cmd file <value>` follow-up message would be redundant.
      if (result && !isCareyRoom) {
        sendMessage(FILE_CMD + ' ' + result.value);
      }
    }
    setUploadingFiles(false);
  }

  async function onAssistantReply() {
    if (loadingReply) return stopRequest();

    let messages = _.flatten(
      groupedMessages.map(({ messages, name }: any) =>
        messages.map(({ text, message }: any) => {
          return `${name}: ${text || message}`;
        }),
      ),
    ).join('\n\n');

    await runSavedPromptTyped(
      'Helpdesk Assistant Reply',
      {
        type: 'chat',
        guidance: currentMessage,
        messages,
      },
      {
        patientId: patient?.id,
      },
    );

    scrollToBottom();
  }

  function handleTypedMessageChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setCurrentMessage(event.target.value);
  }

  function onSentSMS() {
    let message = `Sent SMS: "${currentMessage}"`;
    sendMessage(message);
  }

  function setCurrentMessage(value: string) {
    setValue('currentMessage', value);
  }

  function iconClicked(message: string = currentMessage) {
    if (!!message) sendMessage(message);
  }

  function onKeyDown(event: React.KeyboardEvent, currentMesssage: string) {
    if (event.key !== 'Enter') return;
    // Patient chat (sendOnEnter): plain Enter sends, Shift+Enter inserts a newline.
    if (props.sendOnEnter && !event.shiftKey) {
      event.preventDefault();
      iconClicked(currentMesssage);
      return;
    }
    // Staff chat: Cmd/Ctrl+Enter sends (Enter alone inserts a newline).
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      iconClicked(currentMesssage);
    }
  }

  function sendMessage(text: string) {
    text = text.trim();
    if (props.sendMessage) {
      props.sendMessage(text);
      setCurrentMessage('');
      return true;
    }
  }

  async function startChat() {
    let patientId = patient?.id;
    if (bubble) patientId = bubble.patient_id;

    setCreatingChat(true);
    await dispatch(api.Chat.createOnDemandChatRoom({ patientId }));
    setCreatingChat(false);
    setDisabledChat(false);
  }

  return (
    <View
      {...props}
      currentMessage={currentMessage}
      groupedMessages={groupedMessages}
      disabledChat={disabledChat}
      creatingChat={creatingChat}
      handleTypedMessageChange={handleTypedMessageChange}
      onKeyDown={onKeyDown}
      startChat={startChat}
      setCurrentMessage={setCurrentMessage}
      onSentSMS={onSentSMS}
      iconClicked={iconClicked}
      setMessagesEnd={setMessagesEnd}
      bubble={bubble}
      uploadingFiles={uploadingFiles}
      uploadFiles={allowUpload ? uploadFiles : undefined}
      roomName={roomName}
      isLoadingArchives={isLoadingArchives}
      allowUpload={allowUpload}
      disabledInput={disabledInput}
      onAssistantReply={allowAssistant ? onAssistantReply : undefined}
      assistantLoading={loadingReply}
      extraIcons={extraIcons}
      connected={connected}
      replySuggestions={replySuggestions}
      welcomeMessage={welcomeMessage}
    />
  );
};

export default ChatWindow;
